import { useTranslation } from 'react-i18next';
import Flag from 'react-flags';
import toast from 'react-hot-toast';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaInfoCircle } from 'react-icons/fa';

import BubbleButton from '../../../Buttons/BubbleButton/BubbleButton';
import styles from './JobsCard.module.css';
import { updateFavourites } from '../../../../../app/features/userSlice';
import { useGetJobCategoriesHook } from '../../../../../utils/utilityHooks';
import {
    addJobToFavourites,
    incrementImpressions,
    incrementJobViews,
    removeJobFromFavourites
} from '../../../../../services/jobService';
import {
    Briefcase,
    DollarIcon,
    ExclusiveJobIcon,
    SaveBookMark,
    SaveIcon
} from '../../../../../assets/images/icons';
import {
    getTimeAgo,
    getCurrencySymbol,
    getCurrencySymbolByCountry,
    getCurrencyNameByCountry
} from '../../../../../utils/formatHelpers';

// Currency exchange rates
const EXCHANGE_RATES = {
    AED: 3.67, // 1 USD = 3.67 AED
    SAR: 3.75, // 1 USD = 3.75 SAR
    QAR: 3.64, // 1 USD = 3.64 QAR
    OMR: 0.38, // 1 USD = 0.38 OMR
    KWD: 0.31, // 1 USD = 0.31 KWD
    BHD: 0.38  // 1 USD = 0.38 BHD
};

// List of currency codes to display in tooltip
const CURRENCY_CODES = ['usd', 'aed', 'sar', 'qar', 'omr', 'kwd', 'bhd'];

function JobsCard({ disabled, setFullViewList, filters, onScrollToFullView, fullViewList, job, ...props }) {
    const { t } = useTranslation();
    const userToken = useSelector(state => state.user.token);
    const user = useSelector(state => state.user.user);
    const dispatch = useDispatch();
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipRef = useRef(null);
    const tooltipTimerRef = useRef(null);
    const tooltipHoverRef = useRef(false);

    const desc = job.description;
    const currency = getCurrencySymbol("usd");
    const currencyName = "usd";
    let salary = '';
    let salary_period = t('general.UI.month');
    let date = '';
    
    // Handle tooltip visibility
    const handleTooltipShow = useCallback(() => {
        tooltipHoverRef.current = true;
        if (tooltipTimerRef.current) {
            clearTimeout(tooltipTimerRef.current);
            tooltipTimerRef.current = null;
        }
        setShowTooltip(true);
    }, []);
    
    const handleTooltipHide = useCallback(() => {
        tooltipHoverRef.current = false;
        tooltipTimerRef.current = setTimeout(() => {
            if (!tooltipHoverRef.current) {
                setShowTooltip(false);
            }
        }, 300);
    }, []);
    
    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (tooltipTimerRef.current) {
                clearTimeout(tooltipTimerRef.current);
            }
        };
    }, []);
    
    // Calculate salary in a specific currency
    const calculateSalary = useCallback((amount, currencyCode) => {
        if (currencyCode === 'USD') {
            return amount;
        }
        
        const rate = EXCHANGE_RATES[currencyCode];
        if (!rate) return amount;
        
        if (currencyCode === 'OMR' || currencyCode === 'KWD' || currencyCode === 'BHD') {
            // For these currencies, divide by the rate (USD to currency)
            return Math.round(amount / rate);
        } else {
            // For these currencies, multiply by the rate (USD to currency)
            return Math.round(amount * rate);
        }
    }, []);
    
    // Format salary range for display
    const formatSalaryRange = useCallback((fromValue, toValue, symbol) => {
        if (fromValue === toValue) {
            return `${symbol}${fromValue}`;
        }
        return `${symbol}${fromValue} - ${symbol}${toValue}`;
    }, []);
    
    // Prepare tooltip content with all currency conversions
    const getSalaryInAllCurrencies = useCallback(() => {
        const salaryFrom = job.salary_from;
        const salaryTo = job.salary_to;
        
        const currencies = CURRENCY_CODES.map(code => ({
            code: code.toUpperCase(),
            symbol: getCurrencySymbol(code),
            name: code.toUpperCase()
        }));
        
        return (
            <div className={styles.currencyTooltip}>
                <p className={styles.tooltipTitle}>{t('jobs.salary_in_currencies')}</p>
                {currencies.map(curr => {
                    const fromValue = calculateSalary(salaryFrom, curr.code);
                    const toValue = calculateSalary(salaryTo, curr.code);
                    
                    return (
                        <div key={curr.code} className={styles.currencyRow}>
                            <span className={styles.currencyName}>{curr.name}:</span>
                            <span className={styles.currencyValue}>
                                {formatSalaryRange(fromValue, toValue, curr.symbol)}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }, [job.salary_from, job.salary_to, t, calculateSalary, formatSalaryRange]);

    // Format the primary salary display
    if (job.salary_from !== job.salary_to) {
        salary = currency + job.salary_from + ' - ' + currency + job.salary_to;
    }
    else {
        salary = currency + job.salary_from;
    }

    if (job?.salaryrate) {
        salary_period = t('CompensationAndbenefits.' + job?.salaryrate[0].toUpperCase() + job?.salaryrate.slice(1, job?.salaryrate.length));
    } else if (job?.payment_type === 'hourly') {
        salary_period = t('general.UI.hour');
    }

    if (job.createdAt) {
        const date_info = getTimeAgo(new Date(job.createdAt));
        date = t('general.UI.posted') + ' ';

        if (date_info.unit_plural === 'second' || date_info.unit_plural === 'seconds') {
            date += t('general.UI.just_now');
        }
        else {
            date += date_info.count + ' ' + t('general.UI.' + date_info.unit_plural) + ' ' + t('general.UI.ago');
        }

    }

    let additional_card_classes = '';
    if (disabled) {
        additional_card_classes = styles.disabled;
    }

    let city = job.city;

    if (t('general.city.' + city) !== 'general.city.' + city) {
        city = t('general.city.' + city);
    }

    const profileDataUI = (icon, title, desc, isCenter, showInfoIcon = false) => {
        return (
            <div className={fullViewList ? `${styles.profileDataContainer}` : isCenter ? `${styles.profileDataContainer} ${styles.paddingLeft}` : `${styles.profileDataContainer}`}>
                <div>
                    <div className={styles.dataIconContainer}>
                        <img src={icon} alt={`profile data icon`}
                            className={styles.profileDataIcon}
                        />
                    </div>
                </div>
                <div className={styles.col}>
                    <p className={styles.profileDataTitle}>
                        {title}
                        {showInfoIcon && (
                            <span 
                                className={styles.tooltipContainer}
                                ref={tooltipRef}
                                onMouseEnter={handleTooltipShow}
                                onMouseLeave={handleTooltipHide}
                            >
                                <FaInfoCircle className={styles.infoIcon} />
                                {showTooltip && (
                                    <div 
                                        className={styles.customTooltip}
                                        onMouseEnter={handleTooltipShow}
                                        onMouseLeave={handleTooltipHide}
                                    >
                                        {getSalaryInAllCurrencies()}
                                    </div>
                                )}
                            </span>
                        )}
                    </p>
                    {desc !== 'login_test' && <p className={styles.profileDataValue}>{desc}</p>}
                </div>
            </div>
        )
    }

    const handleAddToFavourites = () => {
        addJobToFavourites({
            job_id: job.id,
            token: userToken
        })
            .then((response) => {
                dispatch(updateFavourites([...user.favourites, parseInt(job.id, 10)]));
                toast.success(t('job.job_added_to_favorites'));
            })
            .catch((error) => {

                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }

            });
    };

    const handleRemoveFromFavourites = () => {
        removeJobFromFavourites({
            job_id: job.id,
            token: userToken
        })
            .then((response) => {
                dispatch(updateFavourites(user.favourites.filter(item => item !== parseInt(job.id, 10))));
                toast.success(t('job.job_removed_from_favorites'));
            })
            .catch((error) => {

                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }

            });
    };

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768); // Change 768px as per your requirement
        };

        handleResize(); // Initial check
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const categoryOptions = useGetJobCategoriesHook();
    const jobCardRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        incrementImpressions(job.id);
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                root: null, // Use the viewport as the root
                threshold: 0.5, // Trigger when 50% of the card is visible
            }
        );

        if (jobCardRef.current) {
            observer.observe(jobCardRef.current);
        }

        return () => {
            if (jobCardRef.current) {
                observer.unobserve(jobCardRef.current);
            }
        };
    }, [job.id]);

    return (
        <div
            ref={jobCardRef}
            className={fullViewList ? `${styles.job_card} ${styles[job.status]} ${additional_card_classes} ${styles.job_cardFullView}` :
                `${styles.job_card} ${styles[job.status]} ${additional_card_classes}`}
            {...props} >
            {(job.status === 'paused' || job.status === 'closed') && (
                <div className={styles.job_card_status}>
                    <span>{t('general.UI.' + job.status)}</span>
                </div>
            )}
            <div className={styles.job_card_head}>
                <div className={styles.job_card_head_info}>
                    <div className={styles.job_card_head_info_main}>
                        <h5 className={styles.jobTitle}>{job.title}
                            {job.exlusiveJob &&
                                <img src={ExclusiveJobIcon} alt="Exclusive Job" />
                            }
                        </h5>
                        <div>

                            <p
                                className={styles.jobDescription}
                                onClick={() => {
                                    window.location.href = `${'employer'}s/${job?.employerDetail?.slug}`;
                                }}
                                style={{
                                    fontSize: fullViewList ? '12px' : '14px',
                                    cursor: "pointer",
                                }}
                            >
                                {job?.employerDetail?.company_name || job?.employerDetail?.first_name + ' ' + job?.employerDetail?.last_name}
                            </p>
                        </div>
                        <div>
                            <Flag
                                name={job.country}
                                format="svg"
                                shiny={false}
                                basePath="/vendor/flags"
                            />
                            <p className={styles.jobDescription} style={{ fontSize: fullViewList ? '12px' : '14px' }}>{t('general.country.' + job.country)}, {city}</p>
                        </div>
                    </div>

                </div>
                <div className={isMobile ? styles.viewButtonWrapperMobile : styles.viewButtonWrapper}>
                    <p className={styles.postedDate} style={{ fontSize: fullViewList ? '10px' : '13px' }}>{date}</p>
                    {/* {!fullViewList && */}
                        <div style={{ display: "flex", gap: '10px' }}>
                            {user?.account_type === 'jobseeker' && (
                                user?.favourites?.includes(parseInt(job.id, 10)) ? (

                                    <img
                                        onClick={handleRemoveFromFavourites}
                                        src={SaveBookMark}
                                        className={styles.bookMarkIcon}
                                        alt={'Notification'}
                                    />
                                ) : (
                                    <img
                                        onClick={handleAddToFavourites}
                                        src={SaveIcon}
                                        className={styles.bookMarkIcon}
                                        alt={'Notification'}
                                    />
                                )

                            )}
                            <BubbleButton
                                onClick={async () => {
                                    await incrementJobViews(job.id);
                                    setFullViewList(job);
                                    const target = document.querySelector('.scrollJobCard')
                                    if (target) {
                                        window.scrollTo({
                                            top: target.offsetTop, behavior: 'smooth'
                                        });
                                    }
                                }}
                                dark small headerDarkButton style={{ width: '100px' }}
                            >
                                {t('general.UI.viewJob')}
                            </BubbleButton>
                        </div>
                    {/* } */}
                </div>
            </div>
            <div className={fullViewList ? `${styles.job_card_body} ${styles.card_body_2} ${styles.card_body_fullView}` : `${styles.job_card_body} ${styles.card_body_2}`} >
                <div className={fullViewList ? styles.columnFullView : styles.column}>
                    {profileDataUI(DollarIcon, t('jobs.salary'), `${salary + "/" + salary_period}`, false, true)}
                </div>
                <div className={fullViewList ? styles.columnFullView : styles.column}>
                    {profileDataUI(Briefcase, t("jobs.job_type"), t('general.UI.' + job.job_type), true)}
                </div>
                <div className={fullViewList ? styles.columnFullView : styles.column} style={{ borderRight: "0px solid black" }}>
                    {profileDataUI(Briefcase, t("jobs.category"), categoryOptions?.filter((e) => e?.value == Number(job?.category))[0]?.label || job?.category, true)}
                </div>
            </div>
            {desc.trim().replace(/<[^>]*>/g, '').length > 0 && (
                <div className={styles.job_card_footer}>
                    {desc.length > 240 ? (
                        <>
                            <div dangerouslySetInnerHTML={{ __html: desc.slice(0, 240) + '... ' }} className={styles.jobDescription}></div>
                            {/* <SimpleLink to={`/jobs/${job.slug}`}>{t('general.UI.show_more')}</SimpleLink> */}
                        </>
                    ) : (
                        <div dangerouslySetInnerHTML={{ __html: desc }}></div>
                    )}
                </div>
            )}

        </div>
    );
}

export default JobsCard;